import React, { useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { CheckSquareOutlined, CloseSquareOutlined } from '@ant-design/icons';
import moment from 'moment';

import styles from '../styles';
import {
  clearAccessoryTransferHistory,
  getAccessoryTransferHistory,
} from '../Actions';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Breadcrumbs from '../Components/Breadcrumbs';
import ApproveModal from './ApproveModal';
import RejectModal from './RejectModal';
import constants from '../constants';

const TransferRequestList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const accessoryTransferHistory = useSelector(
    state => state.accessoryTransferHistory
  );

  const [recordId, setRecordId] = useState(null);
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    dispatch(getAccessoryTransferHistory());
    return () => {
      dispatch(clearAccessoryTransferHistory());
    };
  }, []);

  const showModal = useCallback((id, type) => {
    setRecordId(id);
    setModalType(type);
  }, []);

  const hideModal = useCallback(() => {
    setRecordId(null);
    setModalType(null);
    dispatch(getAccessoryTransferHistory());
  }, []);

  const approveColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(
            constants.permissions.accessoryInventoryTransferAuthorization
          ) ? (
            <Tooltip placement="bottom" title="Approve">
              <CheckSquareOutlined
                className={classes.tableIcon}
                onClick={() => showModal(record.id, 'Approve')}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const rejectColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(
            constants.permissions.accessoryInventoryTransferAuthorization
          ) ? (
            <Tooltip placement="bottom" title="Reject">
              <CloseSquareOutlined
                className={classes.tableIcon}
                onClick={() => showModal(record.id, 'Reject')}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'From',
        children: [
          {
            title: 'Customer',
            dataIndex: 'customer',
            sorter: sorter('customer'),
            onFilter: onFilter('customer'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'BPO #',
            dataIndex: 'bpoNo',
            sorter: sorter('bpoNo'),
            onFilter: onFilter('bpoNo'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Product',
            dataIndex: 'product',
            sorter: sorter('product'),
            onFilter: onFilter('product'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Accessory',
            dataIndex: 'accessory',
            sorter: sorter('accessory'),
            onFilter: onFilter('accessory'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Qty',
            dataIndex: 'quantity',
            sorter: sorter('quantity'),
            onFilter: onFilter('quantity'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Factory',
            dataIndex: 'factory',
            sorter: sorter('factory'),
            onFilter: onFilter('factory'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Rack #',
            dataIndex: 'rackno',
            sorter: sorter('rackno'),
            onFilter: onFilter('rackno'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'To',
        children: [
          {
            title: 'Customer',
            dataIndex: 'targetCustomer',
            sorter: sorter('targetCustomer'),
            onFilter: onFilter('targetCustomer'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'BPO #',
            dataIndex: 'targetBpoNo',
            sorter: sorter('targetBpoNo'),
            onFilter: onFilter('targetBpoNo'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Product',
            dataIndex: 'targetProduct',
            sorter: sorter('targetProduct'),
            onFilter: onFilter('targetProduct'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Accessory',
            dataIndex: 'targetAccessory',
            sorter: sorter('targetAccessory'),
            onFilter: onFilter('targetAccessory'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Qty',
            dataIndex: 'requestedQuantity',
            sorter: sorter('requestedQuantity'),
            onFilter: onFilter('requestedQuantity'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Factory',
            dataIndex: 'targetFactory',
            sorter: sorter('targetFactory'),
            onFilter: onFilter('targetFactory'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Rack #',
            dataIndex: 'targetRackno',
            sorter: sorter('targetRackno'),
            onFilter: onFilter('targetRackno'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Remark',
        dataIndex: 'requestedRemark',
      },
      {
        title: 'Req. By',
        dataIndex: 'requestedBy',
        sorter: sorter('requestedBy'),
        onFilter: onFilter('requestedBy'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Req. At',
        dataIndex: 'requestedAt',
        sorter: dateSorter('requestedAt'),
        onFilter: onDateFilter('requestedAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: requestedAt =>
          requestedAt ? moment(requestedAt).format('DD MMM YYYY') : null,
      },
    ];

    if (
      permissions.includes(
        constants.permissions.accessoryInventoryTransferAuthorization
      )
    ) {
      column.push(
        {
          title: 'Approve',
          width: '5%',
          render: approveColumn,
        },
        {
          title: 'Reject',
          width: '5%',
          render: rejectColumn,
        }
      );
    }

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 2200, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Accessory Inventory"
        moduleLink="/inventory/accessory"
        subModuleName="Transfer Requests List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={accessoryTransferHistory}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
      {modalType === 'Approve' ? (
        <ApproveModal
          recordId={recordId}
          modalType={modalType}
          hideModal={hideModal}
        />
      ) : null}
      {modalType === 'Reject' ? (
        <RejectModal
          recordId={recordId}
          modalType={modalType}
          hideModal={hideModal}
        />
      ) : null}
    </>
  );
};

TransferRequestList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(TransferRequestList);
